import { useLoggedUser, useSessionPermission } from '@btime/react-session'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Fixed } from '../../../packages/kiti/components/helpers/Position'
import { UserAvatar } from '../../../components/presentation/Avatar'
import { useHelpActions } from '../../../utils/contexts/HelpProvider'
import ConfigSubMenu from './ConfigSubMenu'
import CurrentProjectBar from '../../../containers/projects/CurrentProjectBar'
import HomeButton from './HomeButton'
import List from '../../../components/list/List'
import MenuItem from './_shared/MenuItem'
import ProfileSubMenu from './ProfileSubMenu'
import Separator from '../../../packages/kiti/components/helpers/Separator'
import SideMenu from '../../../components/navigation/SideMenu'
import SubMenuItem from './_shared/SubMenuItem'
import { getSubdomain } from '../../../utils/helpers/url'
import { Widget, setWidgetRef } from '@btime/assistant-ui'
import { getAuthToken } from '../../../utils/session/sessionStorage'
import { useNewFeature } from '../../../utils/contexts/NewFeature'
import { useAnalytics } from '../../../utils/analytics/AnalyticsProvider'

const MainMenu = () => {
  const { t } = useTranslation('menu')
  const { openChat, initChat } = useHelpActions()
  const workspace = getSubdomain()
  const user = useLoggedUser()
  const widgetRef = useRef(null)
  const token = getAuthToken()
  const canUseAssistant = useSessionPermission('useBtimeAI')
  const canUseAvailabilityPanel = useSessionPermission('useAvailabilityPanel')
  const { isActive } = useNewFeature('PROCESSES')
  const { initAnalytics } = useAnalytics()

  initAnalytics({ user, workspace })

  initChat({
    user_id: `${user.id}-${workspace}`,
    name: user.name,
    email: user.email,
    created_at: Math.floor(Date.now() / 1000),
    company: { id: workspace, name: workspace, website: `https://${workspace}.btime.io` }
  })

  useEffect(() => {
    if (widgetRef.current) setWidgetRef(widgetRef.current)
  }, [])

  return (
    <>
      {canUseAssistant && (
        <Widget
          ref={widgetRef}
          position="bottom-right"
          width={400}
          height={600}
          welcomeMessage={t('welcomeAssistant')}
          useNativeButton={false}
          token={token}
          workspace={workspace}
        />
      )}

      <Fixed bottom={0} left={3.5} zIndex={0}>
        <CurrentProjectBar />
      </Fixed>

      <SideMenu
        footer={
          <>
            {canUseAssistant && (
              <MenuItem
                icon="IoSparklesOutline"
                label={t('btimeAI')}
                onClick={() => widgetRef?.current?.toggle()}
                highlight={true}
              />
            )}

            <ConfigSubMenu />
            <MenuItem icon="RiQuestionAnswerLine" label={t('chatSupport')} onClick={() => openChat()} />
            <MenuItem tooltip={<ProfileSubMenu />} noPaddingBottom top="auto" noTooltipPadding>
              <UserAvatar user={user} />
            </MenuItem>
          </>
        }
      >
        <HomeButton />
        <Separator weight={4} />

        {!isActive && <MenuItem icon="RiDashboardLine" label={t('dashboard')} path="/" permission="seeDashboard" />}
        {isActive && <MenuItem icon="BsKanban" label={t('processes')} path="/processes" />}

        <MenuItem icon="RiCompass3Line" label={t('map')} path="/maps" permission="seeMap" />

        {!isActive && (
          <MenuItem
            icon="RiInboxLine"
            label={t('serviceOrders')}
            path="/service-orders"
            permission="listServiceOrder"
          />
        )}

        <MenuItem icon="RiClipboardLine" label={t('forms')} path="/forms" permission="listForm" />
        {canUseAvailabilityPanel ? (
          <MenuItem
            icon="RiFileWarningLine"
            label={t('availability')}
            path="/availability"
            permission="useAvailabilityPanel"
          />
        ) : (
          <MenuItem
            icon="RiFileWarningLine"
            label={t('nonCompliance')}
            permission="listNonCompliance"
            top="auto"
            bottom="-3rem"
            noTooltipPadding
            tooltip={
              <List>
                <SubMenuItem icon="RiInboxLine" label={t('nonComplianceServiceOrders')} path="/non-compliances" />
                <SubMenuItem
                  icon="RiClipboardLine"
                  label={t('nonComplianceForms')}
                  path="/non-compliances?origin=formReply"
                />
              </List>
            }
          />
        )}
        <MenuItem
          icon="RiSurveyLine"
          label={t('dynamicServiceOrders')}
          path="/dynamic-service-orders"
          permission="listDynamicServiceOrder"
        />
        <MenuItem icon="RiArchiveLine" label={t('stocks')} path="/stocks" permission="listStocks" />
      </SideMenu>
    </>
  )
}

export default MainMenu
